import "./custom.css";

import { BusinessAnalyst, EpiqAdmin, EpiqProjectManager, EpiqTechSupport, EpiqTemplateAdmin, EpiqUser } from "./constants/roles";

import { Switch, useHistory } from "react-router-dom";
import { UserContext, UserContextType } from "./modules/User/UserContext";

import Admin from "./modules/Admin/Admin";
import ApiAuthorizationRoutes from "./components/api-authorization/ApiAuthorizationRoutes";
import AuthorizeAndAllowRoute from "./components/api-authorization/AuthorizeAndAllowRoute";
import AuthorizeRoute from "./components/api-authorization/AuthorizeRoute";
import CaseDetailPage from "./modules/Cases/CaseDetailPage";
import CaseListPage from "./modules/Cases/CaseListPage";
import { Dashboard } from "./modules/Dashboard/Dashboard";
import ErrorPage from "./components/Shared/ErrorPage";
import InstitutionDetailPage from "./modules/Institutions/InstitutionDetailPage";
import InstitutionListPage from "./modules/Institutions/InstitutionListPage";
import { Layout } from "./components/Layout";
import { useContext, useEffect } from "react";
import RegistrationRequestsPage from "./modules/Users/RegistrationRequestsPage";
import RegistrationReviewUser from "./modules/Users/RegistrationReviewUser";
import Reports from "./components/Reports";
import { Route } from "react-router";
import SubmissionDetailPage from "./modules/Submissions/SubmissionDetailPage";
import SubmissionListPage from "./modules/Submissions/SubmissionListPage";
import TemplateDetailPage from "./modules/Templates/TemplateDetailPage";
import TemplateListPage from "./modules/Templates/TemplateListPage";
import UserListPage from "./modules/Users/UserListPage";
import TermsOfUsePage from "./modules/TermsOfUse/TermsOfUsePage";
import { signal } from "@preact/signals-react";
import { parseBooleanValue } from "./utilities/utilities";
import MailingListPage from "./modules/Mailings/MailingListPage";
import { ApplicationPaths } from "./components/api-authorization/ApiAuthorizationConstants";
import PreRegister from "./modules/Users/PreRegister";
import Register from "./modules/Users/Register";
import ForgotCredentials from "./components/api-authorization/ForgotCredentials";
import ResetCredentials from "./components/api-authorization/ResetCredentials";
import ProfileDialog from "./modules/User/ProfileDialog";
import ChangeCredentials from "./components/api-authorization/ChangeCredentials";

export const mailingSubmissionsFeatureEnabled = signal(false);
export const mailingFileMaxSizeMb = signal(0);
export const reportsMenuFeatureEnabled = signal(false);

export default function App() {
	const { isAuthenticated, isInitialized, user } = useContext(UserContext) as UserContextType;
	const history = useHistory();

	useEffect(() => {
		const populateAppState = async () => {
			try {
				if (!isAuthenticated) return;

				const responseReportsMenu = await fetch(`api/Configuration/GetConfigValueByKey/reportsMenuFeatureEnabled`);

				if (!responseReportsMenu.ok) {
					throw new Error('failed to fetch reports Menu Feature Enabled');
				}

				const configurationReportsMenu = await responseReportsMenu.json();
				reportsMenuFeatureEnabled.value = parseBooleanValue(configurationReportsMenu.value);

				const response = await fetch(`api/Configuration/GetConfigValueByKey/mailingSubmissionsFeatureEnabled`);

				if (!response.ok) {
					throw new Error('failed to fetch mailing Submissions Feature Enabled');
				}

				const configuration = await response.json();
				const value = parseBooleanValue(configuration.value);
				mailingSubmissionsFeatureEnabled.value = value;

				const responseFileSize = await fetch(`api/Configuration/GetConfigValueByKey/mailingFileMaxSizeMb`);

				if (!responseFileSize.ok) {
					throw new Error('failed to fetch mailing File Max Size Mb');
				}

				const configurationFileSize = await responseFileSize.json();
				const valueFileSize = parseInt(configurationFileSize.value, 10);
				mailingFileMaxSizeMb.value = valueFileSize;
			}

			// eat the error
			catch (error) {
				console.error(`populateAppState error: ${error}`);
			}
		};

		populateAppState();
	}, [isAuthenticated]);

	useEffect(() => {
		if (isAuthenticated && window.location.href === (window.location.origin + '/')) {
			history.push("/dashboard");
			return;
		}

		if (isInitialized && !isAuthenticated && window.location.href === (window.location.origin + '/')) {
			history.push(ApplicationPaths.Login);
		}
	}, [isInitialized, isAuthenticated]);

	return (
		<Layout>
			<Switch>
				<Route exact path="/" component={ApiAuthorizationRoutes} />
				<Route exact path="/PreRegister" component={PreRegister} />
				<Route exact path="/ForgotCredentials" component={ForgotCredentials} />
				<Route exact path="/ResetCredentials" component={ResetCredentials} />
				<Route exact path="/ChangeCredentials" component={ChangeCredentials} />
				<Route exact path="/ProfileDialog" component={ProfileDialog} />
				<Route path="/Register/:email" component={Register} />
				<AuthorizeRoute exact path="/CaseList" component={CaseListPage} />
				<AuthorizeRoute exact path="/SubmissionList" component={SubmissionListPage} />
				<AuthorizeRoute exact path="/MailingList" component={MailingListPage} />
				<AuthorizeRoute exact path="/CaseDetail/:caseId/:tabName?" component={CaseDetailPage} />
				<AuthorizeRoute exact path="/Dashboard" component={Dashboard} />
				<AuthorizeRoute path="/SubmissionDetail/:id/:tabName?" component={SubmissionDetailPage} />
				<AuthorizeRoute exact path="/SubmissionList/:type" component={SubmissionListPage} />
				<Route exact path="/TermsOfUse" component={TermsOfUsePage} />

				<AuthorizeAndAllowRoute exact path="/Admin" component={Admin}
					allowedRoles={[EpiqAdmin, EpiqProjectManager, EpiqTechSupport]} />
				<AuthorizeAndAllowRoute exact path="/UserList" component={UserListPage}
					allowedRoles={[EpiqAdmin, EpiqProjectManager, EpiqTechSupport]} />
				<AuthorizeAndAllowRoute exact path="/TemplateDetail/:id/:tabName?" component={TemplateDetailPage}
					allowedRoles={[EpiqUser, EpiqAdmin, EpiqProjectManager, EpiqTechSupport, EpiqTemplateAdmin, BusinessAnalyst]} />
				<AuthorizeAndAllowRoute exact path="/TemplateList" component={TemplateListPage}
					allowedRoles={[EpiqUser, EpiqAdmin, EpiqProjectManager, EpiqTechSupport, EpiqTemplateAdmin, BusinessAnalyst]} />
				<AuthorizeAndAllowRoute exact path="/InstitutionDetail/:id" component={InstitutionDetailPage}
					allowedRoles={[EpiqAdmin, EpiqProjectManager, EpiqTechSupport]} />
				<AuthorizeAndAllowRoute exact path="/InstitutionList" component={InstitutionListPage}
					allowedRoles={[EpiqAdmin, EpiqProjectManager, EpiqTechSupport]} />
				<AuthorizeAndAllowRoute exact path="/ReviewDetail/:registrationId" component={RegistrationReviewUser}
					allowedRoles={[EpiqAdmin, EpiqProjectManager, EpiqTechSupport]} />
				<AuthorizeAndAllowRoute exact path="/RegistrationRequests" component={RegistrationRequestsPage}
					allowedRoles={[EpiqAdmin, EpiqProjectManager, EpiqTechSupport]} />
				{reportsMenuFeatureEnabled.value &&
					<AuthorizeAndAllowRoute exact path="/Reports" component={Reports}
						allowedRoles={[EpiqUser]} />}
				<Route path={ApplicationPaths.ApiAuthorizationPrefix} component={ApiAuthorizationRoutes} />
				<Route path="/errorPage" component={ErrorPage} />
				<Route path="/*" component={ErrorPage} />
			</Switch>
		</Layout>
	);
}